import { useState } from 'react';
import { PERCENTILE_BANDS } from '@leopart/calc';
import { ITestDefinition } from '../../models/testDefinition';
import { Athlete } from './testingTypes';
import { PercentileCell } from '../hooks/useTeamAnalytics';
import styles from './PercentileHeatmap.module.scss';

interface Props {
  categoryTests: ITestDefinition[];
  athletes: Athlete[];
  cells: PercentileCell[];
}

// Soglie fasce percentile dal canone @leopart/calc (PERCENTILE_BANDS 90/70/30).
// Il livello ≥10 resta una sotto-fascia di rendering (5° colore), non di dominio.
function percentileColor(p: number | null): { bg: string; text: string } {
  if (p === null) return { bg: '#f6f6f6', text: '#b3b3b3' };
  if (p >= PERCENTILE_BANDS.high) return { bg: '#1a7a4a', text: '#fff' };
  if (p >= PERCENTILE_BANDS.mid) return { bg: '#51b87a', text: '#fff' };
  if (p >= PERCENTILE_BANDS.low) return { bg: '#f9c74f', text: '#333' };
  if (p >= 10) return { bg: '#f4845f', text: '#fff' };
  return { bg: '#d9534f', text: '#fff' };
}

function formatValue(v: number | null): string {
  if (v === null) return '—';
  return v % 1 === 0 ? String(v) : v.toFixed(1);
}

const MIN_ATHLETES_FOR_PERCENTILE = 5;
const TOOLTIP_NO_PERCENTILE = 'Non è possibile calcolare i percentili con meno di 5 atleti nel gruppo';

export default function PercentileHeatmap({ categoryTests, athletes, cells }: Props) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  if (categoryTests.length === 0 || athletes.length === 0) return null;

  const hasEnoughAthletes = athletes.length >= MIN_ATHLETES_FOR_PERCENTILE;

  const cellMap = new Map<string, PercentileCell>(
    cells.map((c) => [`${c.athleteId}_${c.testId}`, c]),
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.legend}>
        {[
          { label: 'P90+', bg: '#1a7a4a', text: '#fff' },
          { label: 'P70-90', bg: '#51b87a', text: '#fff' },
          { label: 'P30-70', bg: '#f9c74f', text: '#333' },
          { label: 'P10-30', bg: '#f4845f', text: '#fff' },
          { label: '<P10', bg: '#d9534f', text: '#fff' },
        ].map((item) => (
          <span key={item.label} className={styles.legendItem} style={{ background: item.bg, color: item.text }}>
            {item.label}
          </span>
        ))}
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.athleteHeader}>Atleta</th>
              {categoryTests.map((test) => (
                <th key={test.id} className={styles.testHeader} title={test.name}>
                  <span>{test.name}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {athletes.map((athlete) => (
              <tr key={athlete.id}>
                <td className={styles.athleteName}>
                  {athlete.name}
                  {athlete.position && <span className={styles.positionTag}>{athlete.position}</span>}
                </td>
                {categoryTests.map((test) => {
                  const key = `${athlete.id}_${test.id}`;
                  const cell = cellMap.get(key);
                  // CSV08: se <5 atleti i percentili non sono calcolabili — cella neutra con tooltip
                  const percentileForColor = hasEnoughAthletes ? (cell?.percentile ?? null) : null;
                  const colors = percentileColor(percentileForColor);
                  const isOpen = expandedKey === key;

                  const cellTitle = !hasEnoughAthletes
                    ? TOOLTIP_NO_PERCENTILE
                    : cell?.percentile !== null && cell?.percentile !== undefined
                      ? `P${cell.percentile}`
                      : 'Nessun dato';

                  return (
                    <td
                      key={test.id}
                      className={styles.cell}
                      style={{ background: colors.bg, color: colors.text }}
                      onClick={() => setExpandedKey(isOpen ? null : key)}
                      title={cellTitle}
                    >
                      <div className={styles.cellInner}>
                        <span className={styles.cellValue}>{formatValue(cell?.latestValue ?? null)}</span>
                        {cell?.percentile !== null && cell?.percentile !== undefined && (
                          <span className={styles.cellPct}>P{cell.percentile}</span>
                        )}
                      </div>

                      {isOpen && cell && cell.history.length > 0 && (
                        <div
                          className={styles.dropdown}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <p className={styles.dropdownTitle}>Storico — {test.name}</p>
                          <ul className={styles.dropdownList}>
                            {cell.history.slice(0, 8).map((h, i) => (
                              <li key={i} className={styles.dropdownItem}>
                                <span className={styles.dropdownDate}>
                                  {new Date(h.date).toLocaleDateString('it-IT', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: '2-digit',
                                  })}
                                </span>
                                <span className={styles.dropdownVal}>{formatValue(h.value)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
